import * as React from 'react'
import { cn } from '@/lib/utils'
import { Label } from './label'

export interface FieldProps {
  label: React.ReactNode
  /** The id of the control inside, so the label focuses it. */
  htmlFor: string
  /** Short guidance under the control. Hidden while an error shows. */
  hint?: React.ReactNode
  /** Validation message; shown in the danger color under the control. */
  error?: React.ReactNode
  /** Something on the label row's right edge (e.g. a "Forgot password?" link). */
  labelAside?: React.ReactNode
  className?: string
  children: React.ReactNode
}

/**
 * A form field: label stacked above its control, with an optional hint or
 * error below. Every form control goes through this so label placement and
 * spacing match across forms (lint: `app/prefer-field-component`).
 * Give the control `aria-describedby={fieldMessageId(htmlFor)}` to tie it to
 * the hint or error, and `aria-invalid` when there is one.
 */
export function Field({ label, htmlFor, hint, error, labelAside, className, children }: FieldProps) {
  const message = error ?? hint
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={htmlFor}>{label}</Label>
        {labelAside}
      </div>
      {children}
      {message ? (
        <p
          id={fieldMessageId(htmlFor)}
          className={cn('text-caption', error ? 'text-danger' : 'text-muted-foreground')}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}

/** The id `Field` gives its hint/error, for the control's `aria-describedby`. */
export function fieldMessageId(htmlFor: string): string {
  return `${htmlFor}-message`
}
